import { useLayoutEffect, useState, type RefObject } from 'react';

export const DEFAULT_INSPECTOR_WIDTH = 272;
const STORAGE_KEY = 'overland-traces:studio-inspector-width';

export function inspectorWidthLimit(available: number) {
  return Math.max(DEFAULT_INSPECTOR_WIDTH, Math.floor(Math.min(720, available * .55, available - 372)));
}

export function defaultInspectorWidth(available: number) {
  return available >= 1000 ? Math.round(Math.max(400, Math.min(620, available * .3))) : DEFAULT_INSPECTOR_WIDTH;
}

function savedWidth() {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(value) && value >= DEFAULT_INSPECTOR_WIDTH) return Math.min(720, value);
  } catch { /* Storage may be unavailable in private browsing. */ }
  return null;
}

export function useInspectorWidth(parent: RefObject<HTMLDivElement | null>) {
  const [preferred, setPreferred] = useState(savedWidth);
  const [available, setAvailable] = useState(() => innerWidth);
  useLayoutEffect(() => {
    const element = parent.current;
    if (!element) return;
    const measure = () => setAvailable(element.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [parent]);
  const maximum = inspectorWidthLimit(available);
  const defaultWidth = Math.min(defaultInspectorWidth(available), maximum);
  const width = Math.min(preferred ?? defaultWidth, maximum);
  const change = (value: number) => {
    const next = Math.round(Math.max(DEFAULT_INSPECTOR_WIDTH, Math.min(maximum, value)));
    setPreferred(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* Keep resizing available without storage. */ }
  };
  const reset = () => {
    setPreferred(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* Keep the responsive default available without storage. */ }
  };
  return { width, maximum, change, reset };
}
